// services/api_MyFounder.ts - Founder Profile and Submission Management API
import type {
  APIResponse,
  FounderProfile,
  SubmissionProfile,
  CreateFounderProfileRequest,
  UpdateFounderProfileRequest,
  CreateSubmissionProfileRequest,
  UpdateSubmissionProfileRequest,
  OptimizeSubmissionRequest,
  OptimizeSubmissionResponse,
  CoachingRequest,
  CoachingResponse,
  PublishProfileRequest,
  PublishProfileResponse,
  ProfileSearchParams,
  ProfileSearchResult,
  ProfileAnalytics,
  AcceleratorMatch,
  InvestorMatch,
  ProfileNotification,
  ExportOptions,
  ExportResult
} from './MyFounderTypes';

// Base API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ==================== MOCK EXAMPLE DATA ====================

// Example Founder Profile - shown when no profile exists
const MOCK_FOUNDER_PROFILE: FounderProfile = {
  id: 'example-profile-1',
  userId: 'example-user',
  
  // Basic Information
  founderName: 'Example: Sarah Chen',
  email: 'example:sarah@techstartup.com',
  phone: 'example:+1-555-0123',
  location: 'Example: San Francisco, CA',
  linkedIn: 'example:linkedin.com/in/sarahchen',
  twitter: 'example:@sarahchen',
  website: 'example:techstartup.com',
  
  // Company Information
  companyName: 'Example: TechStartup AI',
  companyWebsite: 'example:techstartup.com',
  foundedDate: '2023-01-15',
  companyStage: 'Seed',
  incorporationStatus: 'C-Corp',
  incorporationState: 'Delaware',
  
  // Team
  coFounders: [
    {
      id: 'cofounder-1',
      name: 'Example: Sarah Chen',
      title: 'Example: CEO & Co-Founder',
      background: 'Example: Former ML Engineer at Google, 8 years building AI products. MS Computer Science from Stanford.',
      expertise: ['Example: Machine Learning', 'Example: Product Strategy', 'Example: Enterprise Sales'],
      linkedIn: 'example:linkedin.com/in/sarahchen',
      equity: 45
    },
    {
      id: 'cofounder-2',
      name: 'Example: Michael Rodriguez',
      title: 'Example: CTO & Co-Founder',
      background: 'Example: Tech Lead at Meta, built infrastructure serving 2B+ users. PhD in Distributed Systems from MIT.',
      expertise: ['Example: System Architecture', 'Example: Team Building', 'Example: DevOps'],
      linkedIn: 'example:linkedin.com/in/mrodriguez',
      equity: 45
    }
  ],
  teamSize: 8,
  keyHires: [
    {
      id: 'hire-1',
      role: 'Example: VP of Sales',
      description: 'Example: Enterprise sales leader with 10+ years closing $1M+ deals',
      hiringTimeline: 'Example: Q2 2025',
      salary: 'Example: $180K + equity'
    },
    {
      id: 'hire-2',
      role: 'Example: Senior ML Engineer',
      description: 'Example: Experienced in training large language models at scale',
      hiringTimeline: 'Example: Q1 2025',
      salary: 'Example: $200K + equity'
    }
  ],
  
  // Business Model
  industry: ['Example: Artificial Intelligence', 'Example: B2B SaaS', 'Example: Enterprise Software'],
  targetMarket: 'Example: Mid-market and enterprise companies (500-5000 employees) looking to implement AI automation in their customer service operations',
  businessModel: 'B2B',
  revenueModel: ['Example: Annual subscription ($50K-$500K per year)', 'Example: Usage-based pricing for API calls', 'Example: Professional services for implementation'],
  
  // Traction & Metrics
  currentMRR: 180000,
  currentARR: 2160000,
  monthlyGrowthRate: 22,
  totalUsers: 15000,
  payingCustomers: 47,
  customerAcquisitionCost: 8500,
  lifetimeValue: 125000,
  burnRate: 280000,
  runway: 18,
  
  // Funding
  totalRaised: 3500000,
  currentRound: 'Seed',
  targetRaise: 8000000,
  previousInvestors: [
    {
      id: 'investor-1',
      name: 'Example: Sequoia Capital',
      investorType: 'VC',
      amount: 2000000,
      round: 'Seed',
      date: '2024-03-15'
    },
    {
      id: 'investor-2',
      name: 'Example: Y Combinator',
      investorType: 'Accelerator',
      amount: 500000,
      round: 'Pre-Seed',
      date: '2023-08-01'
    },
    {
      id: 'investor-3',
      name: 'Example: Jason Calacanis',
      investorType: 'Angel',
      amount: 250000,
      round: 'Pre-Seed',
      date: '2023-06-20'
    }
  ],
  
  // Product
  productDescription: 'Example: TechStartup AI is an enterprise-grade AI platform that automates customer service operations, reducing response times by 85% and support costs by 60%. Our proprietary AI models are trained on industry-specific data, providing more accurate and contextual responses than generic solutions.',
  problemSolved: 'Example: Customer service teams are overwhelmed with repetitive inquiries, leading to slow response times (avg 24 hours), high operational costs ($15-25 per ticket), and poor customer satisfaction. Traditional chatbots have 40% accuracy rates, frustrating both customers and support teams.',
  uniqueValueProposition: 'Example: We deliver human-quality customer service at machine speed and scale. Our AI handles 85% of inquiries automatically with 94% accuracy, learns from every interaction, and seamlessly escalates complex issues to human agents with full context.',
  competitiveAdvantage: 'Example: Unlike competitors using generic LLMs, we fine-tune models on each customer\'s specific data, support documents, and past interactions. Our hybrid AI-human workflow ensures accuracy while maintaining the personal touch customers expect.',
  productStage: 'Scaling',
  
  // Market & Competition
  marketSize: 48000000000,
  targetCustomerProfile: 'Example: Fast-growing tech companies and enterprises with 500+ employees, handling 1000+ support tickets monthly, spending $500K+ annually on customer service. Decision makers are VP of Customer Experience, CTO, or COO.',
  competitors: ['Example: Zendesk AI', 'Example: Intercom', 'Example: Ada', 'Example: Ultimate.ai'],
  differentiators: [
    'Example: 94% accuracy vs 65% industry average through custom model fine-tuning',
    'Example: 10x faster implementation (2 weeks vs 6 months for competitors)',
    'Example: Seamless human handoff with full conversation context',
    'Example: Industry-specific AI models (healthcare, fintech, e-commerce)'
  ],
  
  // Milestones & Goals
  keyMilestones: [
    {
      id: 'milestone-1',
      title: 'Example: Reached $2M ARR',
      description: 'Example: Hit our annual recurring revenue target 3 months ahead of schedule',
      date: '2024-11-01',
      status: 'Completed',
      impact: 'Example: Validated product-market fit and pricing strategy'
    },
    {
      id: 'milestone-2',
      title: 'Example: Launched Enterprise Plan',
      description: 'Example: Released SOC 2 Type II compliant enterprise tier with advanced security features',
      date: '2024-09-15',
      status: 'Completed',
      impact: 'Example: Enabled us to close Fortune 500 customers'
    },
    {
      id: 'milestone-3',
      title: 'Example: First Fortune 500 Customer',
      description: 'Example: Signed 3-year contract worth $1.2M with major telecommunications company',
      date: '2024-10-20',
      status: 'Completed',
      impact: 'Example: Validated enterprise sales motion and product readiness'
    },
    {
      id: 'milestone-4',
      title: 'Example: Expand to Healthcare Vertical',
      description: 'Example: Launch HIPAA-compliant version for healthcare customers',
      date: '2025-03-01',
      status: 'In Progress',
      impact: 'Example: Opens $12B addressable market segment'
    }
  ],
  next12MonthGoals: [
    'Example: Grow from $2.1M to $10M ARR',
    'Example: Expand team from 8 to 25 people',
    'Example: Launch in EU market with GDPR compliance',
    'Example: Release mobile apps for iOS and Android',
    'Example: Achieve 95%+ customer retention rate'
  ],
  useOfFunds: 'Example: $3.2M Engineering (40% - expand ML team, build new features), $2.4M Sales & Marketing (30% - hire AEs, increase brand awareness), $1.6M Operations (20% - customer success, finance, legal), $800K Runway Buffer (10%)',
  
  // Additional
  pitchDeckUrl: 'example:drive.google.com/pitch-deck',
  demoVideoUrl: 'example:youtube.com/demo-video',
  productDemoUrl: 'example:app.techstartup.com/demo',
  tags: ['Example: AI', 'Example: B2B SaaS', 'Example: Customer Service', 'Example: Automation'],
  visibility: 'Public',
  
  // Media Files
  media: {
    photos: [
      {
        id: 'photo-1',
        fileName: 'LearnQualify.png',
        fileType: 'image/png',
        mimeType: 'image/png',
        fileSize: 245678,
        fileUrl: '/image_launch/LearnQualify.png',
        uploadedAt: new Date('2024-11-01').toISOString(),
        profileId: 'example-profile-1',
        description: 'Example: Product screenshot showing AI in action',
        thumbnail: '/image_launch/LearnQualify.png'
      },
      {
        id: 'photo-2',
        fileName: 'HoneSuccess.png',
        fileType: 'image/png',
        mimeType: 'image/png',
        fileSize: 198432,
        fileUrl: '/image_launch/HoneSuccess.png',
        uploadedAt: new Date('2024-11-02').toISOString(),
        profileId: 'example-profile-1',
        description: 'Example: Customer success dashboard',
        thumbnail: '/image_launch/HoneSuccess.png'
      }
    ],
    video: {
      id: 'video-1',
      fileName: 'product-demo.mp4',
      fileType: 'video/mp4',
      mimeType: 'video/mp4',
      fileSize: 15678900,
      fileUrl: 'example:youtube.com/demo-video',
      uploadedAt: new Date('2024-11-03').toISOString(),
      profileId: 'example-profile-1',
      description: 'Example: 2-minute product demo video',
      thumbnail: '/image_launch/LearnQualify.png'
    },
    pitchDeck: {
      id: 'deck-1',
      fileName: 'TechStartup-AI-Deck.pdf',
      fileType: 'application/pdf',
      fileSize: 3456789,
      fileUrl: 'example:drive.google.com/pitch-deck',
      uploadedAt: new Date('2024-11-05').toISOString(),
      description: 'Example: 15-slide pitch deck'
    },
    otherDocuments: []
  },
  
  // Metadata
  createdAt: new Date('2024-01-15').toISOString(),
  updatedAt: new Date().toISOString(),
  lastPublishedAt: new Date().toISOString(),
  isPublished: true,
  publishedUrl: 'example:profiles.aiventure.com/techstartup'
};

// Example Submission Profile for Lexicon
const MOCK_SUBMISSION_PROFILE: SubmissionProfile = {
  id: 'example-submission-1',
  userId: 'example-user',
  profileName: 'Example: Y Combinator Application 2025',
  description: 'Example: Tailored application for YC S25 batch focusing on our AI-powered customer service automation platform',
  
  // Application Fields
  companyName: 'Example: TechStartup AI',
  oneLinePitch: 'Example: AI that handles 85% of customer service tickets automatically',
  shortDescription: 'Example: TechStartup AI automates customer service for mid-market companies, reducing response times by 85% and support costs by 60%. Our AI handles routine inquiries while seamlessly escalating complex issues to human agents.',
  longDescription: `Example: Customer service teams at growing companies face an impossible challenge: handling increasing ticket volumes while maintaining quality and keeping costs under control. Traditional solutions fall short—chatbots have 40% accuracy rates, hiring more agents is expensive, and customers wait 24+ hours for responses.

TechStartup AI solves this with enterprise-grade AI that delivers human-quality service at machine speed. Unlike generic chatbots, we fine-tune our models on each customer's specific data, achieving 94% accuracy. We handle 85% of tickets automatically, reducing average response time from 24 hours to under 2 minutes.

Our secret sauce is the hybrid approach: AI handles routine inquiries instantly while learning from every interaction. When complex issues arise, we seamlessly hand off to human agents with full context, eliminating the frustration of customers repeating themselves.

We've grown to $2.1M ARR in 18 months with 47 paying customers including a Fortune 500 telecom company. Our customers see 60% cost reduction and 4.5x improvement in response times. With 22% month-over-month growth and 95%+ retention, we're ready to scale.`,
  
  // Problem & Solution
  problemStatement: 'Example: Customer service teams are overwhelmed with repetitive inquiries, leading to 24-hour response times, high costs ($15-25 per ticket), and poor customer satisfaction. Traditional chatbots only solve 40% of inquiries, frustrating customers and agents alike.',
  solutionDescription: 'Example: We deliver AI-powered automation that handles 85% of customer service tickets with 94% accuracy. Our platform learns from each customer\'s specific data and past interactions, providing contextual responses that feel human. When escalation is needed, we pass full context to agents.',
  whyNow: 'Example: Three converging trends make this the perfect time: (1) LLM breakthroughs enable human-quality AI responses, (2) customer expectations for instant support have skyrocketed post-pandemic, and (3) enterprises are aggressively cutting costs while trying to maintain service quality.',
  
  // Market & Opportunity
  targetMarket: 'Example: Mid-market and enterprise companies (500-5000 employees) in tech, e-commerce, and fintech sectors handling 1000+ monthly support tickets',
  marketSize: 'Example: TAM: $48B (global customer service software), SAM: $12B (AI-powered customer service for mid-market/enterprise), SOM: $2.4B (our target segments and geographies in next 3 years)',
  beachheadMarket: 'Example: Fast-growing B2B SaaS companies with 100-500 employees experiencing rapid growth in support volume',
  goToMarketStrategy: 'Example: Product-led growth with freemium tier (up to 100 tickets/month), inside sales for mid-market ($50K-$200K deals), field sales for enterprise ($200K+). Partnerships with Zendesk, Salesforce, and Slack for distribution.',
  
  // Product
  productStatus: 'Example: Launched and scaling - currently serving 47 paying customers with 15K+ end users',
  keyFeatures: [
    'Example: AI-powered ticket routing and auto-response',
    'Example: Custom model fine-tuning on company data',
    'Example: Seamless AI-to-human handoff with full context',
    'Example: Multi-channel support (email, chat, Slack, API)',
    'Example: Real-time analytics and performance dashboards',
    'Example: Integration with Zendesk, Salesforce, Intercom'
  ],
  technologyStack: [
    'Example: GPT-4 & Claude for base models',
    'Example: Python, FastAPI, PostgreSQL',
    'Example: React, TypeScript, Tailwind CSS',
    'Example: AWS (ECS, RDS, S3, Lambda)',
    'Example: Vector databases (Pinecone) for RAG',
    'Example: Kubernetes for orchestration'
  ],
  intellectualProperty: 'Example: Proprietary fine-tuning methodology for customer service models, patent pending on AI-human handoff system',
  productRoadmap: 'Example: Q1 2025: Mobile apps, voice support; Q2: Healthcare vertical with HIPAA compliance; Q3: International expansion (EU, APAC); Q4: Predictive analytics and customer insights',
  
  // Traction
  tractionSummary: 'Example: $2.1M ARR, 47 paying customers, 22% MoM growth, 95% retention, Fortune 500 customer, handling 250K+ tickets monthly',
  keyMetrics: [
    {
      metricName: 'Example: ARR',
      value: 'Example: $2.1M',
      growthRate: 'Example: 22% MoM',
      context: 'Example: Grown from $0 to $2.1M in 18 months'
    },
    {
      metricName: 'Example: Customers',
      value: 'Example: 47 paying',
      growthRate: 'Example: 8-12 new per month',
      context: 'Example: Average deal size: $45K/year'
    },
    {
      metricName: 'Example: Retention',
      value: 'Example: 95%',
      context: 'Example: Net revenue retention: 125% (expansion revenue)'
    },
    {
      metricName: 'Example: LTV:CAC',
      value: 'Example: 14.7:1',
      context: 'Example: LTV: $125K, CAC: $8.5K, payback period: 4 months'
    }
  ],
  revenueToDate: 'Example: $3.2M total revenue, currently at $180K MRR',
  userGrowth: 'Example: 15,000 end users across 47 companies, processing 250K+ support tickets monthly',
  partnerships: [
    'Example: Technology partnership with Zendesk',
    'Example: Integration partner with Salesforce',
    'Example: Reseller agreement with Slack'
  ],
  mediaOrPress: [
    'Example: Featured in TechCrunch: "AI Startup Raises $3.5M to Revolutionize Customer Service"',
    'Example: Forbes 30 Under 30: Sarah Chen, CEO',
    'Example: VentureBeat: "How TechStartup AI Achieves 94% Accuracy"'
  ],
  
  // Business Model
  revenueStreams: [
    'Example: Subscription SaaS: $50K-$500K per year based on ticket volume',
    'Example: Usage-based: $0.50 per automated ticket above plan limits',
    'Example: Professional services: Implementation and training'
  ],
  pricingModel: 'Example: Starter ($50K/year, up to 10K tickets), Growth ($150K/year, up to 50K tickets), Enterprise (custom pricing, unlimited tickets + dedicated support)',
  unitEconomics: 'Example: Gross margin: 85%, CAC: $8.5K, LTV: $125K, LTV:CAC: 14.7:1, Payback period: 4 months, Annual churn: 5%',
  customerAcquisitionStrategy: 'Example: Content marketing (SEO, thought leadership), Product-led growth (free tier), Inside sales team (5 AEs), Strategic partnerships, Customer referral program (20% of new business)',
  
  // Competition
  competitorAnalysis: 'Example: Zendesk AI (weak AI, complex setup), Intercom (too expensive, limited accuracy), Ada (narrow use cases), Ultimate.ai (European focus). We win on accuracy (94% vs 65%), speed of deployment (2 weeks vs 6 months), and cost (50% less than alternatives).',
  competitiveAdvantages: [
    'Example: 94% accuracy through custom fine-tuning (vs 65% industry avg)',
    'Example: 10x faster implementation (2 weeks vs 6 months)',
    'Example: Seamless human handoff maintains customer satisfaction',
    'Example: Industry-specific models for healthcare, fintech, e-commerce'
  ],
  barriers: [
    'Example: Proprietary fine-tuning methodology',
    'Example: 18 months of training data from 250K+ interactions',
    'Example: Deep integrations with enterprise tools',
    'Example: SOC 2 Type II, GDPR, HIPAA compliance (in progress)'
  ],
  
  // Team
  teamDescription: 'Example: 8-person team with deep expertise in AI/ML and enterprise software. Combined 50+ years of experience at Google, Meta, and leading AI startups. Strong technical co-founders with complementary skills.',
  founderBackgrounds: [
    {
      founderName: 'Example: Sarah Chen',
      title: 'Example: CEO & Co-Founder',
      background: 'Example: 8 years at Google building ML products serving 1B+ users. Led customer service AI team. MS Computer Science, Stanford.',
      relevantExperience: [
        'Example: Built Google Assistant\'s customer service features',
        'Example: Scaled ML systems to billions of users',
        'Example: Led team of 15 ML engineers'
      ],
      education: ['Example: MS Computer Science, Stanford', 'Example: BS Mathematics, MIT'],
      achievements: [
        'Example: Forbes 30 Under 30',
        'Example: Published 5 papers on conversational AI',
        'Example: Google\'s "Founders Award" for innovation'
      ]
    },
    {
      founderName: 'Example: Michael Rodriguez',
      title: 'Example: CTO & Co-Founder',
      background: 'Example: Tech Lead at Meta building infrastructure for 2B+ users. 10 years in distributed systems. PhD MIT.',
      relevantExperience: [
        'Example: Architected Meta\'s real-time messaging infrastructure',
        'Example: Built systems handling 10M+ requests/second',
        'Example: Team of 20 engineers across 3 continents'
      ],
      education: ['Example: PhD Distributed Systems, MIT', 'Example: MS Computer Engineering, Carnegie Mellon'],
      achievements: [
        'Example: Meta "Hacker Cup" winner',
        'Example: 3 patents in distributed systems',
        'Example: ACM Distinguished Member'
      ]
    }
  ],
  advisors: [
    {
      name: 'Example: Dr. Jane Smith',
      background: 'Example: Former VP of AI at Salesforce, Stanford AI Lab researcher',
      howHelping: 'Example: AI strategy, enterprise sales, board observer'
    },
    {
      name: 'Example: David Park',
      background: 'Example: Founder of CustomerIO (acquired for $200M), expert in customer success',
      howHelping: 'Example: Go-to-market strategy, fundraising connections'
    }
  ],
  whyThisTeam: 'Example: We uniquely combine world-class AI expertise with deep customer service domain knowledge. Sarah built customer service AI at Google scale. Michael built infrastructure for billions of users at Meta. We\'ve worked together for 5 years and have complementary skills: Sarah on product/AI, Michael on infrastructure/scale.',
  
  // Funding
  fundingHistory: 'Example: Pre-seed: $750K (Aug 2023 - Jason Calacanis + angels), Seed: $2.75M (Mar 2024 - Sequoia Capital led), Total raised: $3.5M',
  currentRaise: {
    roundType: 'Series A',
    amount: 8000000,
    date: '2025-Q2',
    investors: [],
    valuation: 40000000,
    terms: 'Example: SAFE note, 20% discount, $40M cap'
  },
  previousRounds: [
    {
      roundType: 'Seed',
      amount: 2750000,
      date: '2024-03-15',
      investors: ['Example: Sequoia Capital', 'Example: Y Combinator'],
      valuation: 15000000
    },
    {
      roundType: 'Pre-Seed',
      amount: 750000,
      date: '2023-08-01',
      investors: ['Example: Jason Calacanis', 'Example: Various Angels'],
      valuation: 5000000
    }
  ],
  capitalEfficiency: 'Example: Reached $2.1M ARR on just $3.5M raised. Current burn: $280K/month, 18 months runway. Every dollar invested generates $0.60 in ARR.',
  
  // Vision & Strategy
  visionStatement: 'Example: To become the AI brain powering customer service for every company on the planet. We believe every customer interaction should be instant, accurate, and delightful—whether handled by AI or human.',
  threeYearPlan: 'Example: Year 1: Reach $10M ARR, expand to 150+ customers, launch healthcare vertical. Year 2: $35M ARR, international expansion (EU, APAC), launch voice support. Year 3: $100M ARR, become category leader, IPO or acquisition potential.',
  exitStrategy: 'Example: Multiple paths: (1) IPO in 5-7 years at $1B+ valuation, (2) Strategic acquisition by Salesforce, Microsoft, or ServiceNow at $500M-$1B, (3) Stay independent and build generational company',
  
  // Specific Asks
  whatYouNeed: 'Example: $8M Series A to: (1) Expand engineering team 8→25 people, (2) Build enterprise sales team, (3) Launch healthcare and international markets, (4) Extend runway to 24+ months',
  idealInvestorProfile: 'Example: Series A lead with: (1) Enterprise SaaS experience, (2) Portfolio companies in customer service or AI, (3) Hands-on help with GTM and recruiting, (4) $3-5M check size, (5) Board seat',
  networkingAsk: 'Example: Intros to: (1) VPs of Customer Experience at Fortune 500 companies, (2) Head of Sales at enterprise SaaS companies for hiring, (3) Product leaders at Zendesk, Salesforce for partnerships',
  
  // Use of Funds
  useOfFunds: 'Example: $3.2M (40%) Engineering - hire 12 ML engineers, 5 product engineers. $2.4M (30%) Sales & Marketing - hire 8 AEs, 2 marketing, increase brand awareness. $1.6M (20%) Operations - customer success team, finance, legal, HR. $800K (10%) Buffer - extend runway to 24+ months.',
  fundingBreakdown: [
    {
      category: 'Example: Engineering & Product',
      amount: 3200000,
      percentage: 40,
      description: 'Example: Hire 12 ML engineers, 5 product engineers. Build healthcare vertical, mobile apps, voice support.'
    },
    {
      category: 'Example: Sales & Marketing',
      amount: 2400000,
      percentage: 30,
      description: 'Example: Hire 8 AEs, 1 VP Sales, 2 marketing. Attend conferences, run campaigns, build brand.'
    },
    {
      category: 'Example: Operations',
      amount: 1600000,
      percentage: 20,
      description: 'Example: Customer success team (5 people), finance, legal, HR, office space.'
    },
    {
      category: 'Example: Runway Buffer',
      amount: 800000,
      percentage: 10,
      description: 'Example: Safety buffer to extend runway to 24+ months, weather any market conditions.'
    }
  ],
  
  // Accelerator-Specific
  whyThisAccelerator: 'Example: Y Combinator is the perfect fit because: (1) Strong AI/ML portfolio and expertise, (2) Enterprise SaaS success stories like Brex, Rippling, (3) Unmatched network for fundraising and hiring, (4) Peer group of ambitious founders tackling hard problems',
  programFit: 'Example: We\'re at the perfect stage for YC: product-market fit proven ($2.1M ARR), need help scaling GTM and fundraising for Series A. We\'re coachable, technical, and ready to grow 10x in next 12 months.',
  commitmentAvailability: 'Example: Both founders 100% committed, based in SF Bay Area, no other time commitments',
  locationPreference: 'Example: San Francisco (current location), willing to relocate team if needed',
  
  // Tags & Categorization
  industries: ['Example: Artificial Intelligence', 'Example: B2B SaaS', 'Example: Customer Service', 'Example: Enterprise Software'],
  technologies: ['Example: GPT-4', 'Example: Machine Learning', 'Example: NLP', 'Example: Cloud Infrastructure'],
  
  // Media Files
  media: {
    photos: [
      {
        id: 'submission-photo-1',
        fileName: 'LearnQualify.png',
        fileType: 'image/png',
        mimeType: 'image/png',
        fileSize: 245678,
        fileUrl: '/image_launch/LearnQualify.png',
        uploadedAt: new Date('2024-12-01').toISOString(),
        profileId: 'example-submission-1',
        description: 'Example: Product screenshot for application',
        thumbnail: '/image_launch/LearnQualify.png'
      },
      {
        id: 'submission-photo-2',
        fileName: 'HoneSuccess.png',
        fileType: 'image/png',
        mimeType: 'image/png',
        fileSize: 198432,
        fileUrl: '/image_launch/HoneSuccess.png',
        uploadedAt: new Date('2024-12-01').toISOString(),
        profileId: 'example-submission-1',
        description: 'Example: Dashboard metrics',
        thumbnail: '/image_launch/HoneSuccess.png'
      }
    ],
    video: {
      id: 'submission-video-1',
      fileName: 'yc-application-video.mp4',
      fileType: 'video/mp4',
      mimeType: 'video/mp4',
      fileSize: 15678900,
      fileUrl: 'example:youtube.com/yc-demo',
      uploadedAt: new Date('2024-12-01').toISOString(),
      profileId: 'example-submission-1',
      description: 'Example: 1-minute application video',
      thumbnail: '/image_launch/LearnQualify.png'
    },
    pitchDeck: {
      id: 'submission-deck-1',
      fileName: 'YC-Application-Deck.pdf',
      fileType: 'application/pdf',
      mimeType: 'application/pdf',
      fileSize: 3456789,
      fileUrl: 'example:drive.google.com/yc-deck',
      uploadedAt: new Date('2024-12-01').toISOString(),
      profileId: 'example-submission-1',
      description: 'Example: YC application pitch deck'
    },
    financialModel: {
      id: 'submission-model-1',
      fileName: 'Financial-Model.xlsx',
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      fileSize: 987654,
      fileUrl: 'example:sheets.google.com/financial-model',
      uploadedAt: new Date('2024-12-01').toISOString(),
      description: 'Example: 3-year financial projections'
    },
    otherDocuments: []
  },
  
  // Metadata
  createdAt: new Date('2024-12-01').toISOString(),
  updatedAt: new Date().toISOString(),
  lastOptimizedAt: new Date().toISOString(),
  optimizationScore: 92,
  
  aiSuggestions: [
    {
      id: 'suggestion-1',
      field: 'oneLinePitch',
      suggestion: 'Example: Add specific metric to make impact more concrete',
      reason: 'Example: Numbers make pitches more memorable and credible',
      createdAt: new Date().toISOString(),
      applied: true
    }
  ],
  coachingNotes: [
    'Example: Strong traction metrics - highlight the Fortune 500 customer more',
    'Example: Consider adding more detail on why now is the right time',
    'Example: Excellent team background - make sure investors see this early'
  ]
};

// Authentication helper
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  
  if (!token) {
    console.warn('No auth token found in localStorage. User may need to log in.');
  } else {
    console.log('Using auth token:', token.substring(0, 20) + '...');
  }
  
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Normalize a backend profile object so the frontend can consume it.
// The backend stores `profile_id` / `user_id` while the frontend expects `id` / `userId`.
const normalizeProfile = (raw: any): any => {
  if (!raw) return raw;
  const { profile_id, user_id, ...rest } = raw;
  return {
    ...rest,
    id: profile_id ?? raw.id ?? '',
    userId: user_id ?? raw.userId ?? '',
  };
};

// Build a full FounderProfile (with media) from a raw DynamoDB item.
// Transforms the flat `files` array into the `ProfileMedia` structure.
const buildFounderProfile = (raw: any): FounderProfile => {
  if (!raw) return raw;
  const normalized = normalizeProfile(raw);
  const files = normalized.files || [];

  const mapFile = (f: any) => f ? ({
    id: f.id, fileName: f.fileName, fileType: f.fileType,
    mimeType: f.mimeType, fileSize: f.fileSize, fileUrl: f.fileUrl,
    uploadedAt: f.createdAt || '', profileId: f.profileId, description: f.description,
  }) : undefined;

  const photos = files.filter((f: any) => f.fileType === 'photo').map(mapFile);
  const videoFile = files.find((f: any) => f.fileType === 'video');
  const pitchDeck = files.find((f: any) => f.fileType === 'document' && f.documentType === 'pitch_deck');
  const financialModel = files.find((f: any) => f.fileType === 'document' && f.documentType === 'financial_model');
  const otherDocs = files.filter((f: any) => f.fileType === 'document' && !['pitch_deck', 'financial_model'].includes(f.documentType));

  return {
    ...normalized,
    // SubmissionProfile → FounderProfile content field mappings
    uniqueValueProposition: normalized.uniqueValueProposition ?? normalized.oneLinePitch ?? '',
    productDescription: normalized.productDescription ?? normalized.longDescription ?? normalized.shortDescription ?? '',
    problemSolved: normalized.problemSolved ?? normalized.problemStatement ?? '',
    companyStage: normalized.companyStage ?? normalized.productStatus ?? '',
    productStage: normalized.productStage ?? normalized.productStatus ?? '',
    industry: normalized.industry ?? normalized.industries ?? [],
    founderName: normalized.founderName ?? normalized.profileName ?? '',
    differentiators: normalized.differentiators ?? (normalized.competitiveAdvantages ? [normalized.competitiveAdvantages] : []),
    competitors: normalized.competitors ?? (normalized.competitorAnalysis ? [normalized.competitorAnalysis] : []),
    competitiveAdvantage: normalized.competitiveAdvantage ?? normalized.competitiveAdvantages ?? '',
    // Map founderBackgrounds → coFounders
    coFounders: normalized.coFounders ?? (normalized.founderBackgrounds || []).map((fb: any) => ({
      id: fb.founderName || '',
      name: fb.founderName || '',
      title: fb.title || '',
      background: fb.background || '',
      expertise: fb.relevantExperience || [],
    })),
    // ── New field mappings (Plan Phase 4) ──
    email: normalized.founderEmail || normalized.email || '',
    phone: normalized.founderPhone || normalized.phone || '',
    location: normalized.founderLocation || normalized.location || '',
    companyWebsite: normalized.companyWebsite || '',
    foundedDate: normalized.foundedDate || '',
    incorporationStatus: normalized.incorporationStatus || 'Not Incorporated',
    tags: normalized.tags || [],
    businessModel: normalized.businessModel || 'Other',
    revenueModel: normalized.revenueModel || [],
    targetCustomerProfile: normalized.targetCustomerProfile || '',
    marketSize: normalized.marketSizeNumber || (typeof normalized.marketSize === 'string' ? parseFloat((normalized.marketSize as string).replace(/[^0-9.]/g, '')) || 0 : normalized.marketSize) || 0,
    currentMRR: normalized.currentMRR ?? normalized.current_mrr ?? undefined,
    currentARR: normalized.currentARR ?? normalized.current_arr ?? undefined,
    monthlyGrowthRate: normalized.monthlyGrowthRate ?? normalized.monthly_growth_rate ?? undefined,
    totalUsers: normalized.totalUsers ?? normalized.total_users ?? undefined,
    payingCustomers: normalized.payingCustomers ?? normalized.paying_customers ?? undefined,
    customerAcquisitionCost: normalized.customerAcquisitionCost ?? normalized.customer_acquisition_cost ?? undefined,
    lifetimeValue: normalized.lifetimeValue ?? normalized.lifetime_value ?? undefined,
    burnRate: normalized.burnRate ?? normalized.burn_rate ?? undefined,
    runway: normalized.runway ?? undefined,
    teamSize: normalized.teamSize ?? normalized.team_size ?? 0,
    keyHires: normalized.keyHires || normalized.key_hires || [],
    totalRaised: normalized.totalRaised ?? normalized.total_raised ?? 0,
    currentRound: normalized.currentRound || normalized.current_round || '',
    targetRaise: normalized.targetRaise ?? normalized.target_raise ?? 0,
    previousInvestors: normalized.previousInvestors || normalized.previous_investors || [],
    keyMilestones: normalized.keyMilestones || normalized.key_milestones || [],
    next12MonthGoals: normalized.next12MonthGoals || normalized.next_12_month_goals || [],
    pitchDeckUrl: normalized.pitchDeckUrl || normalized.pitch_deck_url || '',
    demoVideoUrl: normalized.demoVideoUrl || normalized.demo_video_url || '',
    productDemoUrl: normalized.productDemoUrl || normalized.product_demo_url || '',
    visibility: normalized.visibility || 'Public',
    // snake_case → camelCase system field mappings
    isPublished: normalized.is_published ?? normalized.isPublished ?? false,
    publishedUrl: normalized.published_url ?? normalized.publishedUrl,
    createdAt: normalized.created_at ?? normalized.createdAt ?? '',
    updatedAt: normalized.updated_at ?? normalized.updatedAt ?? '',
    lastPublishedAt: normalized.published_at ?? normalized.lastPublishedAt,
    media: {
      photos,
      video: mapFile(videoFile),
      pitchDeck: mapFile(pitchDeck),
      financialModel: mapFile(financialModel),
      otherDocuments: otherDocs.map(mapFile),
    },
  };
};

// Ensure every field accessed by InvestorFounderSearch.tsx has a safe default.
const ensureProfileDefaults = (profile: FounderProfile): FounderProfile => {
  let safeMarketSize = profile.marketSize;
  if (typeof safeMarketSize === 'string') {
    const cleaned = (safeMarketSize as string).replace(/[^0-9.]/g, '');
    safeMarketSize = parseFloat(cleaned) || 0;
  }
  if (safeMarketSize == null || isNaN(safeMarketSize as number)) safeMarketSize = 0;

  return {
    ...profile,
    industry: Array.isArray(profile.industry) ? profile.industry : [],
    coFounders: Array.isArray(profile.coFounders) ? profile.coFounders : [],
    previousInvestors: Array.isArray(profile.previousInvestors) ? profile.previousInvestors : [],
    tags: Array.isArray(profile.tags) ? profile.tags : [],
    competitors: Array.isArray(profile.competitors) ? profile.competitors : [],
    differentiators: Array.isArray(profile.differentiators) ? profile.differentiators : [],
    keyMilestones: Array.isArray(profile.keyMilestones) ? profile.keyMilestones : [],
    next12MonthGoals: Array.isArray(profile.next12MonthGoals) ? profile.next12MonthGoals : [],
    keyHires: Array.isArray(profile.keyHires) ? profile.keyHires : [],
    revenueModel: Array.isArray(profile.revenueModel) ? profile.revenueModel : [],
    totalRaised: profile.totalRaised ?? 0,
    targetRaise: profile.targetRaise ?? 0,
    teamSize: profile.teamSize ?? 0,
    marketSize: safeMarketSize as number,
    founderName: profile.founderName || 'Unknown Founder',
    companyName: profile.companyName || 'Unknown Company',
    location: profile.location || '',
    companyStage: profile.companyStage || 'Idea',
    incorporationStatus: profile.incorporationStatus || 'Not Incorporated',
    foundedDate: profile.foundedDate || new Date().toISOString(),
    productDescription: profile.productDescription || '',
    problemSolved: profile.problemSolved || '',
    uniqueValueProposition: profile.uniqueValueProposition || '',
    competitiveAdvantage: profile.competitiveAdvantage || '',
    businessModel: profile.businessModel || 'Other',
    productStage: profile.productStage || 'Concept',
    targetMarket: profile.targetMarket || '',
    targetCustomerProfile: profile.targetCustomerProfile || '',
    useOfFunds: profile.useOfFunds || '',
    email: profile.email || '',
    phone: profile.phone || '',
    media: profile.media || { photos: [], video: undefined, pitchDeck: undefined, financialModel: undefined, otherDocuments: [] },
    createdAt: profile.createdAt || new Date().toISOString(),
    updatedAt: profile.updatedAt || new Date().toISOString(),
    isPublished: profile.isPublished ?? true,
    visibility: profile.visibility || 'Public',
    // New field defaults (Plan Phase 4)
    companyWebsite: profile.companyWebsite || '',
    demoVideoUrl: profile.demoVideoUrl || '',
    productDemoUrl: profile.productDemoUrl || '',
    pitchDeckUrl: profile.pitchDeckUrl || '',
    currentRound: profile.currentRound || '',
  };
};

// Response handler
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorText = await response.text();
    const error: any = new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    error.status = response.status;
    error.statusText = response.statusText;
    throw error;
  }
  return response.json();
};

// Safe fetch wrapper that handles network errors gracefully
const safeFetch = async (url: string, options: RequestInit = {}): Promise<Response | null> => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error: any) {
    console.error('Network error - backend not available:', error);
    // Return null for network errors (no backend available)
    if (error.name === 'TypeError' || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      return null;
    }
    throw error;
  }
};

// ==================== FOUNDER SERVICE CLASS ====================

export class FounderService {
  constructor() {}

  // ==================== FOUNDER PROFILE OPERATIONS ====================

  /**
   * Get the current user's founder profile
   */
  async getMyFounderProfile(entityId: string = 'aiVentureCoach'): Promise<APIResponse<FounderProfile>> {
    try {
      console.log(`Fetching founder profile for entity: ${entityId}`);
      
      const params = new URLSearchParams({ entityid: entityId });
      const response = await safeFetch(`${API_BASE_URL}/founder/profile?${params}`, {
        headers: getAuthHeaders()
      });
      
      // Handle case where backend is not available
      if (response === null) {
        console.log('Backend not available - returning example profile for demonstration');
        return {
          success: true,
          data: MOCK_FOUNDER_PROFILE,
          message: 'Example profile (backend not available)'
        };
      }
      
      // Handle 404 as "no profile yet" - return example
      if (response.status === 404) {
        console.log('No founder profile found (404) - returning example profile');
        return {
          success: true,
          data: MOCK_FOUNDER_PROFILE,
          message: 'Example profile (no profile created yet)'
        };
      }
      
      const data = await handleResponse(response);
      console.log('Founder profile response:', data);

      const profiles = (data.profiles || []);
      if (profiles.length === 0) {
        return {
          success: true,
          data: MOCK_FOUNDER_PROFILE,
          message: 'Example profile (no profile created yet)'
        };
      }
      return { success: true, data: buildFounderProfile(profiles[0]) };
    } catch (error: any) {
      console.error('Error fetching founder profile:', error);
      
      // If it's a 404, return example profile
      if (error.status === 404) {
        return {
          success: true,
          data: MOCK_FOUNDER_PROFILE,
          message: 'Example profile (no profile created yet)'
        };
      }
      
      // For network errors, return example profile (don't throw)
      if (error.name === 'TypeError' || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.log('Network error - returning example profile');
        return {
          success: true,
          data: MOCK_FOUNDER_PROFILE,
          message: 'Example profile (backend not available)'
        };
      }
      
      throw error;
    }
  }

  /**
   * Get all founder profiles for the current user (for multi-profile selector)
   */
  async getAllMyProfiles(entityId: string = 'aiVentureCoach'): Promise<APIResponse<FounderProfile[]>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        headers: getAuthHeaders()
      });
      if (response === null) {
        return { success: true, data: [MOCK_FOUNDER_PROFILE] };
      }
      if (response.status === 404) {
        return { success: true, data: [MOCK_FOUNDER_PROFILE] };
      }
      const data = await handleResponse(response);
      const profiles = (data.profiles || []).map(buildFounderProfile);
      if (profiles.length === 0) {
        return { success: true, data: [MOCK_FOUNDER_PROFILE] };
      }
      return { success: true, data: profiles };
    } catch (error: any) {
      console.error('Error fetching all profiles:', error);
      if (error.name === 'TypeError' || error.message?.includes('Failed to fetch')) {
        return { success: true, data: [MOCK_FOUNDER_PROFILE] };
      }
      throw error;
    }
  }

  /**
   * Create a new founder profile
   */
  async createFounderProfile(
    profileData: CreateFounderProfileRequest,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<FounderProfile>> {
    try {
      console.log('Creating founder profile with data:', profileData);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...profileData, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please configure your API endpoint and ensure the server is running.');
      }
      
      const data = await handleResponse(response);
      console.log('Founder profile creation response:', data);
      
      return {
        success: true,
        data: data.profile
      };
    } catch (error) {
      console.error('Error creating founder profile:', error);
      throw error;
    }
  }

  /**
   * Update founder profile
   */
  async updateFounderProfile(
    profileId: string,
    updates: Partial<FounderProfile>,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<FounderProfile>> {
    try {
      console.log('Updating founder profile:', profileId, updates);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...updates, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.profile
      };
    } catch (error) {
      console.error('Error updating founder profile:', error);
      throw error;
    }
  }

  /**
   * Delete founder profile
   */
  async deleteFounderProfile(
    profileId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting founder profile:', error);
      throw error;
    }
  }

  /**
   * Publish founder profile to make it visible to investors
   */
  async publishFounderProfile(
    request: PublishProfileRequest,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<PublishProfileResponse>> {
    try {
      console.log('Publishing founder profile:', request);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${request.profileId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...request, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error publishing founder profile:', error);
      throw error;
    }
  }

  /**
   * Unpublish founder profile
   */
  async unpublishFounderProfile(
    profileId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/unpublish`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      await handleResponse(response);
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error unpublishing founder profile:', error);
      throw error;
    }
  }

  /**
   * Get profile analytics
   */
  async getProfileAnalytics(
    profileId: string,
    timeRange: string = '30d'
  ): Promise<APIResponse<ProfileAnalytics>> {
    try {
      const params = new URLSearchParams({ timeRange });
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/analytics?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response === null || response.status === 404) {
        // Return empty analytics for development
        return {
          success: true,
          data: {
            profileId,
            views: 0,
            uniqueVisitors: 0,
            investorViews: 0,
            savedByInvestors: 0,
            contactRequests: 0,
            averageTimeOnPage: 0,
            topReferrers: [],
            viewsByDate: [],
            viewsByCountry: []
          }
        };
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching profile analytics:', error);
      // Return empty analytics instead of failing
      return {
        success: true,
        data: {
          profileId,
          views: 0,
          uniqueVisitors: 0,
          investorViews: 0,
          savedByInvestors: 0,
          contactRequests: 0,
          averageTimeOnPage: 0,
          topReferrers: [],
          viewsByDate: [],
          viewsByCountry: []
        }
      };
    }
  }

  // ==================== SUBMISSION PROFILE OPERATIONS (LEXICON) ====================

  /**
   * Get all submission profiles for the current user
   */
  async getSubmissionProfiles(entityId: string = 'aiVentureCoach'): Promise<APIResponse<SubmissionProfile[]>> {
    try {
      console.log(`Fetching submission profiles for entity: ${entityId}`);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        headers: getAuthHeaders()
      });

      // Handle case where backend is not available
      if (response === null) {
        console.log('Backend not available - returning example submission profile');
        return {
          success: true,
          data: [MOCK_SUBMISSION_PROFILE],
          message: 'Example profile (backend not available)'
        };
      }

      // Handle 404 as "no profiles yet" - return example
      if (response.status === 404) {
        console.log('No submission profiles found (404) - returning example profile');
        return {
          success: true,
          data: [MOCK_SUBMISSION_PROFILE],
          message: 'Example profile (no profiles created yet)'
        };
      }

      const data = await handleResponse(response);
      console.log('Submission profiles response:', data);

      const profiles = (data.profiles || []).map(normalizeProfile);
      return {
        success: true,
        data: profiles
      };
    } catch (error: any) {
      console.error('Error fetching submission profiles:', error);

      // If it's a 404, return example profile
      if (error.status === 404) {
        return {
          success: true,
          data: [MOCK_SUBMISSION_PROFILE],
          message: 'Example profile (no profiles created yet)'
        };
      }

      // For network errors, return example profile (don't throw)
      if (error.name === 'TypeError' || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.log('Network error - returning example profile');
        return {
          success: true,
          data: [MOCK_SUBMISSION_PROFILE],
          message: 'Example profile (backend not available)'
        };
      }

      throw error;
    }
  }

  /**
   * Get a specific submission profile
   */
  async getSubmissionProfile(profileId: string): Promise<APIResponse<SubmissionProfile>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile?profile_id=${profileId}`, {
        headers: getAuthHeaders()
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: normalizeProfile(data.profile)
      };
    } catch (error) {
      console.error('Error fetching submission profile:', error);
      throw error;
    }
  }

  /**
   * Create a new submission profile
   */
  async createSubmissionProfile(
    profileData: CreateSubmissionProfileRequest,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      // Unwrap the nested "profile" key so the backend receives flat fields
      const flatData = profileData.profile ?? profileData;
      console.log('Creating submission profile with data:', flatData);

      const response = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...flatData, entityid: entityId })
      });

      if (response === null) {
        throw new Error('Backend not available. Please configure your API endpoint and ensure the server is running.');
      }

      const data = await handleResponse(response);
      console.log('Submission profile creation response:', data);

      return {
        success: true,
        data: normalizeProfile(data.profile)
      };
    } catch (error) {
      console.error('Error creating submission profile:', error);
      throw error;
    }
  }

  /**
   * Update submission profile
   */
  async updateSubmissionProfile(
    profileId: string,
    updates: Partial<SubmissionProfile>,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      console.log('Updating submission profile:', profileId, updates);

      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...updates, entityid: entityId })
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: normalizeProfile(data.profile)
      };
    } catch (error) {
      console.error('Error updating submission profile:', error);
      throw error;
    }
  }

  /**
   * Delete submission profile
   */
  async deleteSubmissionProfile(
    profileId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting submission profile:', error);
      throw error;
    }
  }

  /**
   * Duplicate submission profile
   */
  async duplicateSubmissionProfile(
    profileId: string,
    newName: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      // Fetch the original profile, then create a copy with the new name
      const origResponse = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        headers: getAuthHeaders()
      });

      if (origResponse === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const origData = await handleResponse(origResponse);
      const original = (origData.profiles || []).find(
        (p: any) => (p.profile_id ?? p.id) === profileId
      );

      if (!original) {
        throw new Error('Original profile not found');
      }

      // Strip system fields and set the new name
      const { profile_id: _pid, user_id: _uid, id: _id, created_at: _ca, updated_at: _ua, ...copyData } = original;
      copyData.profileName = newName;

      const response = await safeFetch(`${API_BASE_URL}/founder/profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...copyData, entityid: entityId })
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: normalizeProfile(data.profile)
      };
    } catch (error) {
      console.error('Error duplicating submission profile:', error);
      throw error;
    }
  }

  // ==================== AI OPTIMIZATION & COACHING ====================

  /**
   * Optimize submission profile using AI
   */
  async optimizeSubmission(
    request: OptimizeSubmissionRequest,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<OptimizeSubmissionResponse>> {
    try {
      console.log('Optimizing submission profile:', request);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${request.profileId}/optimize`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...request, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. AI optimization requires a server connection.');
      }
      
      const data = await handleResponse(response);
      console.log('Optimization response:', data);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error optimizing submission:', error);
      throw error;
    }
  }

  /**
   * Get AI coaching insights
   */
  async getCoaching(
    request: CoachingRequest,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<CoachingResponse>> {
    try {
      console.log('Getting coaching insights:', request);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/coaching`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...request, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. AI coaching requires a server connection.');
      }
      
      const data = await handleResponse(response);
      console.log('Coaching response:', data);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error getting coaching:', error);
      throw error;
    }
  }

  /**
   * Apply AI suggestion to submission profile
   */
  async applySuggestion(
    profileId: string,
    suggestionId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      const response = await safeFetch(
        `${API_BASE_URL}/founder/profile/${profileId}/suggestions/${suggestionId}/apply`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ entityid: entityId })
        }
      );
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.profile
      };
    } catch (error) {
      console.error('Error applying suggestion:', error);
      throw error;
    }
  }

  // ==================== MATCHING & RECOMMENDATIONS ====================

  /**
   * Get accelerator matches for a submission profile
   */
  async getAcceleratorMatches(
    profileId: string,
    limit: number = 10
  ): Promise<APIResponse<AcceleratorMatch[]>> {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      
      const response = await safeFetch(
        `${API_BASE_URL}/founder/profile/${profileId}/matches/accelerators?${params}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      if (response === null || response.status === 404) {
        return {
          success: true,
          data: []
        };
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.matches || []
      };
    } catch (error) {
      console.error('Error fetching accelerator matches:', error);
      return {
        success: true,
        data: []
      };
    }
  }

  /**
   * Get investor matches for founder profile
   */
  async getInvestorMatches(
    profileId: string,
    limit: number = 10
  ): Promise<APIResponse<InvestorMatch[]>> {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      
      const response = await safeFetch(
        `${API_BASE_URL}/founder/profile/${profileId}/matches/investors?${params}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      if (response === null || response.status === 404) {
        return {
          success: true,
          data: []
        };
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.matches || []
      };
    } catch (error) {
      console.error('Error fetching investor matches:', error);
      return {
        success: true,
        data: []
      };
    }
  }

  // ==================== SEARCH & DISCOVERY ====================

  /**
   * Search published founder profiles (for investors)
   */
  async searchFounderProfiles(
    params: ProfileSearchParams
  ): Promise<APIResponse<ProfileSearchResult>> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.query) searchParams.append('query', params.query);
      if (params.industries?.length) searchParams.append('industries', params.industries.join(','));
      if (params.companyStage?.length) searchParams.append('stages', params.companyStage.join(','));
      if (params.location?.length) searchParams.append('locations', params.location.join(','));
      if (params.fundingRange) {
        searchParams.append('minFunding', params.fundingRange[0].toString());
        searchParams.append('maxFunding', params.fundingRange[1].toString());
      }
      if (params.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      
      const response = await safeFetch(`${API_BASE_URL}/founder/search?${searchParams}`, {
        headers: getAuthHeaders()
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error searching founder profiles:', error);
      throw error;
    }
  }

  /**
   * Get a published founder profile by URL slug or ID
   */
  async getPublishedProfile(identifier: string): Promise<APIResponse<FounderProfile>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/public/${identifier}`, {
        headers: getAuthHeaders()
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: buildFounderProfile(data.profile)
      };
    } catch (error) {
      console.error('Error fetching published profile:', error);
      throw error;
    }
  }

  /**
   * Get the owner's DRAFT profile (working copy) by ID — used for preview mode.
   */
  async getDraftProfile(profileId: string): Promise<APIResponse<FounderProfile>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}`, {
        headers: getAuthHeaders()
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: buildFounderProfile(data.profile)
      };
    } catch (error) {
      console.error('Error fetching draft profile:', error);
      throw error;
    }
  }

  /**
   * Revert the draft profile to the currently-published snapshot (discard unpublished changes).
   */
  async revertFounderProfile(
    profileId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/revert`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ entityid: entityId })
      });

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: normalizeProfile(data.profile)
      };
    } catch (error) {
      console.error('Error reverting founder profile:', error);
      throw error;
    }
  }

  // ==================== NOTIFICATIONS ====================

  /**
   * Get notifications for the current user
   */
  async getNotifications(
    read?: boolean,
    limit: number = 50
  ): Promise<APIResponse<ProfileNotification[]>> {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (read !== undefined) params.append('read', read.toString());
      
      const response = await safeFetch(`${API_BASE_URL}/founder/notifications?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response === null || response.status === 404) {
        return {
          success: true,
          data: []
        };
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.notifications || []
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return {
        success: true,
        data: []
      };
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsRead(): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/notifications/read-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      return {
        success: true
      };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // ==================== EXPORT & SHARING ====================

  /**
   * Export profile in various formats
   */
  async exportProfile(
    profileId: string,
    options: ExportOptions
  ): Promise<APIResponse<ExportResult>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/export`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(options)
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error exporting profile:', error);
      throw error;
    }
  }

  /**
   * Generate shareable link for profile
   */
  async generateShareLink(
    profileId: string,
    expiresIn?: number
  ): Promise<APIResponse<{ shareUrl: string; expiresAt: string }>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/share`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ expiresIn })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error generating share link:', error);
      throw error;
    }
  }

  // ==================== FILE UPLOAD OPERATIONS ====================

  /**
   * Upload a file (photo, video, document)
   */
  async uploadFile(
    file: File,
    fileType: 'photo' | 'video' | 'pitchDeck' | 'financialModel' | 'document',
    entityId: string = 'aiVentureCoach',
    description?: string
  ): Promise<APIResponse<FileUploadResponse>> {
    try {
      console.log('Uploading file:', file.name, 'Type:', fileType);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      formData.append('entityid', entityId);
      if (description) formData.append('description', description);
      
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await safeFetch(`${API_BASE_URL}/founder/upload`, {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (response === null) {
        throw new Error('Backend not available. File uploads require a server connection.');
      }
      
      const data = await handleResponse(response);
      console.log('File upload response:', data);
      
      return {
        success: true,
        data: {
          fileId: data.fileId,
          fileUrl: data.fileUrl,
          fileName: data.fileName,
          fileSize: data.fileSize
        }
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Validate profile completeness
   */
  async validateProfile(profileId: string): Promise<APIResponse<{
    isComplete: boolean;
    completionPercentage: number;
    missingFields: string[];
    recommendations: string[];
  }>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/validate`, {
        headers: getAuthHeaders()
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error validating profile:', error);
      throw error;
    }
  }

  /**
   * Get profile templates
   */
  async getProfileTemplates(
    category?: string
  ): Promise<APIResponse<SubmissionProfile[]>> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      
      const response = await safeFetch(`${API_BASE_URL}/founder/templates?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response === null || response.status === 404) {
        return {
          success: true,
          data: []
        };
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.templates || []
      };
    } catch (error) {
      console.error('Error fetching templates:', error);
      return {
        success: true,
        data: []
      };
    }
  }

  /**
   * Create submission from template
   */
  async createFromTemplate(
    templateId: string,
    profileName: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<SubmissionProfile>> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/templates/${templateId}/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ profileName, entityid: entityId })
      });
      
      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }
      
      const data = await handleResponse(response);
      
      return {
        success: true,
        data: data.profile
      };
    } catch (error) {
      console.error('Error creating from template:', error);
      throw error;
    }
  }

  // ==================== FILE UPLOAD OPERATIONS ====================

  /**
   * Upload a file via presigned S3 URL (bypasses API Gateway size limits).
   * 1. Request presigned URL from backend
   * 2. PUT file directly to S3
   * 3. Confirm upload with backend to record metadata
   */
  private async presignedUpload(
    profileId: string,
    file: File,
    fileType: 'photo' | 'video' | 'document',
    documentType?: string
  ): Promise<APIResponse<any>> {
    // Step 1: Get presigned URL from backend
    const urlResponse = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/upload-url`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fileType,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        documentType,
      })
    });

    if (urlResponse === null) {
      throw new Error('Backend not available. Please try again when the server is running.');
    }

    const urlData = await handleResponse(urlResponse);

    // Step 2: Upload file directly to S3 (no auth header — presigned URL IS the auth)
    const s3Response = await fetch(urlData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!s3Response.ok) {
      throw new Error(`File upload failed: ${s3Response.status} ${s3Response.statusText}`);
    }

    // Step 3: Confirm upload with backend
    const confirmResponse = await safeFetch(`${API_BASE_URL}/founder/profile/${profileId}/confirm-upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        fileId: urlData.fileId,
        fileType,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        s3Key: urlData.fileKey,
        documentType,
      })
    });

    if (confirmResponse === null) {
      throw new Error('Backend not available. Upload may have succeeded but was not confirmed.');
    }

    const data = await handleResponse(confirmResponse);
    return { success: true, data: data.file };
  }

  /**
   * Upload a photo for submission profile (max 2)
   */
  async uploadPhoto(
    profileId: string,
    file: File,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<any>> {
    try {
      console.log('Uploading photo:', file.name);
      return await this.presignedUpload(profileId, file, 'photo');
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  /**
   * Upload a video for submission profile (max 1)
   */
  async uploadVideo(
    profileId: string,
    file: File,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<any>> {
    try {
      console.log('Uploading video:', file.name);
      return await this.presignedUpload(profileId, file, 'video');
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  /**
   * Upload a document (pitch deck, financial model, etc.)
   */
  async uploadDocument(
    profileId: string,
    file: File,
    documentType: 'pitch_deck' | 'financial_model' | 'other',
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<any>> {
    try {
      console.log('Uploading document:', file.name);
      return await this.presignedUpload(profileId, file, 'document', documentType);
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Delete an uploaded file
   */
  async deleteFile(
    profileId: string,
    fileId: string,
    entityId: string = 'aiVentureCoach'
  ): Promise<APIResponse<void>> {
    try {
      const response = await safeFetch(
        `${API_BASE_URL}/founder/profile/${profileId}/files/${fileId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({ entityid: entityId })
        }
      );

      if (response === null) {
        throw new Error('Backend not available. Please try again when the server is running.');
      }

      return {
        success: true
      };
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Get all uploaded files for a profile
   */
  async getProfileFiles(profileId: string): Promise<APIResponse<{
    photos: UploadedFile[];
    videos: UploadedFile[];
    documents: UploadedFile[];
  }>> {
    try {
      const response = await safeFetch(
        `${API_BASE_URL}/founder/profile/${profileId}/files`,
        {
          headers: getAuthHeaders()
        }
      );

      if (response === null || response.status === 404) {
        return {
          success: true,
          data: {
            photos: [],
            videos: [],
            documents: []
          }
        };
      }

      const data = await handleResponse(response);

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error fetching profile files:', error);
      return {
        success: true,
        data: {
          photos: [],
          videos: [],
          documents: []
        }
      };
    }
  }

  // ==================== SEARCH OPERATIONS ====================

  /**
   * Fetch all published profiles from the backend (GET, public endpoint).
   * Returns [] on any error — never throws.
   */
  private async fetchPublishedProfiles(): Promise<FounderProfile[]> {
    try {
      const response = await safeFetch(`${API_BASE_URL}/founder/search?limit=1000`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response === null || !response.ok) {
        console.log('Backend unavailable for published profiles fetch');
        return [];
      }
      const data = await response.json();
      const rawProfiles: any[] = data.profiles || [];
      return rawProfiles.map((raw: any) => ensureProfileDefaults(buildFounderProfile(raw)));
    } catch (error) {
      console.error('Error fetching published profiles:', error);
      return [];
    }
  }

  /**
   * Client-side filter / sort / paginate over a set of profiles.
   */
  private searchProfilesClientSide(
    allProfiles: FounderProfile[],
    params: ProfileSearchParams
  ): APIResponse<ProfileSearchResult> {
    let filteredProfiles = [...allProfiles];

    // Apply filters
    if (params.query) {
      const query = params.query.toLowerCase();
      filteredProfiles = filteredProfiles.filter(profile => {
        const searchableText = [
          profile.founderName,
          profile.companyName,
          profile.productDescription,
          profile.problemSolved,
          profile.uniqueValueProposition,
          profile.targetMarket,
          ...(profile.industry || []),
          ...(profile.tags || [])
        ].join(' ').toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (params.industries && params.industries.length > 0) {
      filteredProfiles = filteredProfiles.filter(profile =>
        (profile.industry || []).some((ind: string) =>
          params.industries!.some(paramInd =>
            ind.toLowerCase().includes(paramInd.toLowerCase())
          )
        )
      );
    }

    if (params.companyStage && params.companyStage.length > 0) {
      filteredProfiles = filteredProfiles.filter(profile =>
        params.companyStage!.includes(profile.companyStage)
      );
    }

    if (params.location && params.location.length > 0) {
      filteredProfiles = filteredProfiles.filter(profile =>
        params.location!.some(loc =>
          (profile.location || '').toLowerCase().includes(loc.toLowerCase())
        )
      );
    }

    if (params.fundingRange) {
      const [min, max] = params.fundingRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.totalRaised >= min && profile.totalRaised <= max
      );
    }

    if (params.teamSize) {
      const [min, max] = params.teamSize;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.teamSize >= min && profile.teamSize <= max
      );
    }

    if (params.arrRange) {
      const [min, max] = params.arrRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.currentARR && profile.currentARR >= min && profile.currentARR <= max
      );
    }

    if (params.growthRange) {
      const [min, max] = params.growthRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.monthlyGrowthRate && profile.monthlyGrowthRate >= min && profile.monthlyGrowthRate <= max
      );
    }

    if (params.marketSizeRange) {
      const [min, max] = params.marketSizeRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.marketSize >= min && profile.marketSize <= max
      );
    }

    if (params.usersRange) {
      const [min, max] = params.usersRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.totalUsers && profile.totalUsers >= min && profile.totalUsers <= max
      );
    }

    if (params.customersRange) {
      const [min, max] = params.customersRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.payingCustomers && profile.payingCustomers >= min && profile.payingCustomers <= max
      );
    }

    if (params.runwayRange) {
      const [min, max] = params.runwayRange;
      filteredProfiles = filteredProfiles.filter(profile =>
        profile.runway && profile.runway >= min && profile.runway <= max
      );
    }

    if (params.hasRevenue !== undefined) {
      filteredProfiles = filteredProfiles.filter(profile =>
        params.hasRevenue ? (profile.currentARR && profile.currentARR > 0) : true
      );
    }

    // Apply sorting
    const sortBy = params.sortBy || 'recent';
    filteredProfiles.sort((a, b) => {
      switch (sortBy) {
        case 'traction':
          return (b.currentARR || 0) - (a.currentARR || 0);
        case 'funding':
          return b.totalRaised - a.totalRaised;
        case 'recent':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        case 'relevance':
        default:
          return 0;
      }
    });

    // Apply pagination
    const page = params.page || 1;
    const limit = params.limit || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedProfiles = filteredProfiles.slice(startIndex, endIndex);

    return {
      success: true,
      data: {
        profiles: paginatedProfiles,
        total: filteredProfiles.length,
        page,
        totalPages: Math.ceil(filteredProfiles.length / limit),
        filters: {
          industries: params.industries || [],
          stages: params.companyStage || [],
          locations: params.location || [],
          fundingRange: params.fundingRange
        }
      }
    };
  }

  /**
   * Search and filter founder profiles.
   * Fetches published profiles from the database and filters client-side.
   */
  async searchProfiles(
    params: ProfileSearchParams
  ): Promise<APIResponse<ProfileSearchResult>> {
    try {
      const realProfiles = await this.fetchPublishedProfiles();
      return this.searchProfilesClientSide(realProfiles, params);
    } catch (error) {
      console.error('Error in searchProfiles:', error);
      return {
        success: true,
        data: { profiles: [], total: 0, page: 1, totalPages: 0, filters: { industries: [], stages: [], locations: [] } }
      };
    }
  }

  /**
   * Get unique filter values for search UI
   */
  async getFilterOptions(): Promise<APIResponse<{
    industries: string[];
    stages: string[];
    locations: string[];
    businessModels: string[];
    productStages: string[];
  }>> {
    try {
      // Try API endpoint first
      const response = await safeFetch(`${API_BASE_URL}/founder/filters`, {
        headers: getAuthHeaders()
      });

      if (response !== null && response.ok) {
        const data = await handleResponse(response);
        return {
          success: true,
          data: data
        };
      }

      // Derive filter options from real published profiles
      const realProfiles = await this.fetchPublishedProfiles();
      const industries = [...new Set(realProfiles.flatMap(p => p.industry || []))];
      const stages = [...new Set(realProfiles.map(p => p.companyStage).filter(Boolean))];
      const locations = [...new Set(realProfiles.map(p => {
        const parts = (p.location || '').split(',');
        return parts[parts.length - 1].trim();
      }).filter(Boolean))];
      const businessModels = [...new Set(realProfiles.map(p => p.businessModel).filter(Boolean))];
      const productStages = [...new Set(realProfiles.map(p => p.productStage).filter(Boolean))];

      return {
        success: true,
        data: {
          industries: industries.sort(),
          stages: stages.sort(),
          locations: locations.sort(),
          businessModels: businessModels.sort(),
          productStages: productStages.sort()
        }
      };
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  }
}

// Export service instance
export const founderService = new FounderService();
export default founderService;